import { useEffect, useLayoutEffect } from 'react';
import { create } from 'zustand';
import { wallpaperPref } from '@omnia/shared';
import { api } from '../lib/api.ts';
import { usePrefsQuery } from '../infobar/prefsStore.ts';
import { DEFAULT_WALLPAPER, findWallpaper, resolveWallpaperId, type Wallpaper } from './index.ts';

const userCacheKey = (userId: string) => `omnia.wallpaper.${userId}`;
/** The last wallpaper shown on this device, so the sign-in screen and a fresh session's first frame match it. */
const DEVICE_CACHE_KEY = 'omnia.wallpaper';

interface WallpaperStore {
  id: string;
  userId: string | null;
  /** A choice was made since sign-in, so a slower server answer must not overwrite it. */
  edited: boolean;
  load: (userId: string | null) => void;
  applyServer: (raw: unknown) => void;
  select: (id: string) => void;
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(userId: string | null, id: string) {
  try {
    localStorage.setItem(DEVICE_CACHE_KEY, id);
    if (userId) localStorage.setItem(userCacheKey(userId), id);
  } catch {
    // The server copy is the source of truth; the cache only speeds up first paint.
  }
}

export const useWallpaperStore = create<WallpaperStore>((set, get) => ({
  id: resolveWallpaperId(read(DEVICE_CACHE_KEY) ?? undefined),
  userId: null,
  edited: false,
  load: (userId) => {
    if (get().userId === userId) return;
    const cached = userId ? read(userCacheKey(userId)) : null;
    set({ userId, edited: false, id: resolveWallpaperId(cached ?? read(DEVICE_CACHE_KEY) ?? undefined) });
  },
  applyServer: (raw) => {
    const { userId, edited } = get();
    if (!userId || edited) return;
    const parsed = wallpaperPref.safeParse(raw);
    // Nothing stored (or unreadable) means the default, not whatever the cache held.
    const id = parsed.success ? resolveWallpaperId(parsed.data.id) : DEFAULT_WALLPAPER;
    write(userId, id);
    set({ id });
  },
  select: (id) => {
    const { userId } = get();
    set({ id, edited: true });
    write(userId, id);
    if (!userId) return;
    api('/api/prefs/wallpaper', { method: 'PUT', body: { id } }).catch(() => {
      // Kept locally; the next change tries again.
    });
  },
}));

/** The wallpaper picture to draw, or null for the plain grid desk. */
export function useWallpaper(): Wallpaper | null {
  return useWallpaperStore((state) => findWallpaper(state.id)) ?? null;
}

/** Loads the signed-in user's wallpaper: the local cache before first paint, then the server copy. */
export function useWallpaperSync(userId: string | null) {
  useLayoutEffect(() => useWallpaperStore.getState().load(userId), [userId]);
  const stored = usePrefsQuery(userId);
  useEffect(() => {
    if (stored.isSuccess) useWallpaperStore.getState().applyServer(stored.data.wallpaper);
  }, [stored.isSuccess, stored.data]);
}
